import { DataGrid, DropdownButton, Logo, Menu } from '@neo4j-ndl/react';
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import pricing_1_0 from './data/aura_pricing_1.0.json';
import pricing_1_1 from './data/aura_pricing_1.1.json';
import pricing_1_2 from './data/aura_pricing_1.2.json';

interface PricingData {
  'product-name': string;
  'pricing-plan': string;
  capacity: string;
  'unit-of-measure': string;
  'price-acu': string;
  'consumption-sku': string;
  notes?: string;
}

const columnHelper = createColumnHelper<PricingData>();

type VersionKey = '1.0' | '1.1' | '1.2';
type VersionData = {
  [key in VersionKey]: {
    data: PricingData[];
    name: string;
    effectiveDate: string;
  };
};

const versionMap: VersionData = {
  '1.0': {
    data: pricing_1_0,
    name: 'Version 1.0',
    effectiveDate: 'Valid until September 3, 2024',
  },
  '1.1': {
    data: pricing_1_1,
    name: 'Version 1.1',
    effectiveDate: 'Valid from September 4, 2024',
  },
  '1.2': {
    data: pricing_1_2,
    name: 'Version 1.2',
    effectiveDate: 'Valid from May 1, 2025',
  },
};

export const AuraPricingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pricingData, setPricingData] = useState<PricingData[]>(pricing_1_2);
  const [version, setVersion] = useState<VersionKey>('1.2');
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    const urlVersion = searchParams.get('version');
    if (urlVersion !== null && (urlVersion === '1.0' || urlVersion === '1.1' || urlVersion === '1.2')) {
      setVersion(urlVersion);
      // Type assertion to ensure compatibility with the state type
      setPricingData(versionMap[urlVersion].data);
    } else {
      setVersion('1.2');
      setPricingData(pricing_1_2);
    }
  }, [searchParams]);

  const handleVersionChange = (selectedVersion: string) => {
    setIsOpen(false);
    searchParams.set('version', selectedVersion);
    setSearchParams(searchParams);
  };

  const columns = [
    columnHelper.accessor('product-name', {
      header: 'Product name',
      cell: (info) => info.getValue(),
      minSize: 150,
      size: 180,
    }),
    columnHelper.accessor('pricing-plan', {
      header: 'Pricing plan',
      cell: (info) => info.getValue(),
      minSize: 220,
      size: 230,
    }),
    columnHelper.accessor('capacity', {
      header: 'Capacity',
      cell: (info) => info.getValue(),
      minSize: 150,
      size: 150,
    }),
    columnHelper.accessor('unit-of-measure', {
      header: 'Unit of measure  ',
      cell: (info) => info.getValue(),
      minSize: 150,
      size: 150,
    }),
    columnHelper.accessor('price-acu', {
      header: 'Price (ACU)*',
      cell: (info) => info.getValue(),
      minSize: 150,
      size: 180,
    }),
    columnHelper.accessor('consumption-sku', {
      header: 'Consumption SKU',
      cell: (info) => info.getValue(),
      minSize: 370,
      size: 380,
    }),
    columnHelper.accessor('notes', {
      header: 'Notes',
      cell: (info) => info.getValue(),
      minSize: 150,
      size: 150,
      enableHiding: true,
    }),
  ];

  const data = useMemo(() => {
    return pricingData;
  }, [pricingData]);

  const table = useReactTable({
    data: data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: true,
    getSortedRowModel: getSortedRowModel(),
  });

  const getValidDate = () => {
    return versionMap[version].effectiveDate;
  };

  return (
    <div className="mx-auto flex flex-col justify-center">
      <div className="bg-palette-neutral-bg-weak overflow-auto rounded-md p-10 drop-shadow-md">
        <div className="flex items-center justify-between">
          <Logo width={150} height={48} />
          <div className="flex items-center">
            <div className="mr-2">Select version:</div>
            <DropdownButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} ref={anchorRef} className="w-fit">
              {versionMap[version].name}
              <Menu
                placement="bottom-end-top-end"
                isOpen={isOpen}
                anchorRef={anchorRef}
                isPortaled={false}
                minWidth={270}
                className="w-[270px]"
                onClose={() => setIsOpen(false)}
              >
                <Menu.Items>
                  {Object.entries(versionMap).map(([ver, info]) => (
                    <Menu.Item
                      key={ver}
                      title={info.name}
                      description={info.effectiveDate}
                      onClick={() => handleVersionChange(ver)}
                      className="w-[250px]"
                    />
                  ))}
                </Menu.Items>
              </Menu>
            </DropdownButton>
          </div>
        </div>

        <h4 className="mt-8">{`Neo4j Aura Pricing - ${versionMap[version].name}`}</h4>
        {getValidDate() && <p className="mb-8 text-sm">{getValidDate()}</p>}
        <p className="my-6 text-xs">{'* 1 ACU (Aura Consumption Unit) = 1 USD'}</p>
        <DataGrid
          isResizable={false}
          tableInstance={table}
          components={{ Navigation: null }}
          isKeyboardNavigable={false}
        />
      </div>
    </div>
  );
};
